//@flow
import * as Immutable from 'immutable';

import { RiderPersonaLimits } from 'common/models/flow/RiderPersonaLimits';
import type { RiderPersonaLimitsModelType } from 'common/models/flow/RiderPersonaLimits';

import { VolumeLimit } from './VolumeLimit';
import { RiderPersonaResourceLimit } from './RiderPersonaResourceLimit';
import { ResourceLimit } from './ResourceLimit';

import { Units } from 'common/enums/UnitEnum';
import { DataUtils } from 'common/utils/DataUtils';
import NumberUtils from 'common/utils/NumberUtils';

import { UnitEnum } from 'common/models/internal/RiderPersonaResourceLimit';

import {
    validatable,
    softValidateAll,
    validateAll,
    validate
} from 'common/utils/ValidationUtil';

import I18nHelper from 'common/helpers/I18nHelper';
const translationsNamespace = ['entityManagement', 'validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const METERS_CUBIC = 'METERS_CUBIC';

const units = {
    volume: {
        preferredUnits: [Units.list.volume.CubicMeter],
        sourceUnit: Units.list.volume.CubicMeter
    },
    distance: {
        preferredUnits: [Units.list.length.Kilometer, Units.list.length.Mile],
        sourceUnit: Units.list.length.Meter
    },
    weight: {
        preferredUnits: [Units.list.mass.Kilogram, Units.list.mass.Pound],
        sourceUnit: Units.list.mass.Kilogram
    },
    travelTime: {
        preferredUnits: [Units.list.time.Minute],
        sourceUnit: Units.list.time.Second
    },
    totalTime: {
        preferredUnits: [Units.list.time.Minute],
        sourceUnit: Units.list.time.Second
    }
};

@validatable
class InternalRiderPersonaLimits extends RiderPersonaLimits {
    static fromJS(
        json: RiderPersonaLimitsModelType
    ): InternalRiderPersonaLimits {
        const state: Object = Object.assign({}, json);
        state.volume = state.volume
            ? VolumeLimit.fromJS(state.volume)
            : state.volume;
        state.distance = state.distance
            ? RiderPersonaResourceLimit.fromJS(state.distance)
            : state.distance;
        state.weight = state.weight
            ? RiderPersonaResourceLimit.fromJS(state.weight)
            : state.weight;
        state.travelTime = state.travelTime
            ? RiderPersonaResourceLimit.fromJS(state.travelTime)
            : state.travelTime;
        state.totalTime = state.totalTime
            ? RiderPersonaResourceLimit.fromJS(state.totalTime)
            : state.totalTime;
        state.location = state.location
            ? RiderPersonaResourceLimit.fromJS(state.location)
            : state.location;
        state.task = state.task
            ? RiderPersonaResourceLimit.fromJS(state.task)
            : state.task;
        state.tour = state.tour
            ? RiderPersonaResourceLimit.fromJS(state.tour)
            : state.tour;
        state.resourceLimits = Immutable.List(state.resourceLimits).map(item =>
            ResourceLimit.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    getPreferredUnit(unitConverter: Object, type: string) {
        switch (type) {
            case 'tour':
            case 'task':
            case 'location':
                return 'COUNT';
            default:
                return unitConverter.getPreferredUnit({
                    preferredUnits: units[type]?.preferredUnits,
                    sourceUnit: units[type]?.sourceUnit
                });
        }
    }

    getPreferredUnitText(unitConverter: Object, type: string) {
        switch (type) {
            case 'tour':
            case 'task':
            case 'location':
                return 'COUNT';
            default:
                return unitConverter.getConvertedUnitDisplayText({
                    preferredUnits: units[type]?.preferredUnits,
                    sourceUnit: units[type]?.sourceUnit,
                    pluralizeUnit: false
                });
        }
    }

    getPreferredValue(options) {
        const { unitConverter, value, type } = options;
        const targetUnit = this.getPreferredUnit(unitConverter, type);
        const { sourceUnit } = units[type] || {};
        if (!DataUtils.isEmpty(value) && NumberUtils.isNumeric(value)) {
            return unitConverter.basicConvert({
                value,
                sourceUnit,
                targetUnit
            });
        } else {
            return value;
        }
    }

    getSourceValue(options) {
        const { unitConverter, value, type } = options;
        const sourceUnit = this.getPreferredUnit(unitConverter, type);
        const { sourceUnit: targetUnit } = units[type] || {};
        if (!DataUtils.isEmpty(value) && NumberUtils.isNumeric(value)) {
            return unitConverter.basicConvert({
                value,
                sourceUnit,
                targetUnit
            });
        } else {
            return value;
        }
    }

    getValue(options) {
        const { toSource, ...rest } = options;
        if (toSource) {
            return this.getSourceValue(rest);
        } else {
            return this.getPreferredValue(rest);
        }
    }

    getConvertedResourceFn(unitConverter, toSource: boolean = false) {
        const limits = this;
        return (resource, type) => {
            return resource
                .setHigherThresh(
                    limits.getValue({
                        toSource,
                        value: resource.higherThresh,
                        unitConverter,
                        type
                    })
                )
                .setLowerThresh(
                    limits.getValue({
                        toSource,
                        value: resource.lowerThresh,
                        unitConverter,
                        type
                    })
                )
                .setAllValidationErrors(); // Hack to set validation errors after setting higher and lower limits
        };
    }

    convertUnit(unitConverter, toSource: boolean = false, unitSelected): this {
        let {
            volume,
            distance,
            weight,
            travelTime,
            totalTime,
            location
        } = this;
        const { task, tour } = this;
        const getConvertedResource = this.getConvertedResourceFn(
            unitConverter,
            toSource
        );

        if (
            !(
                DataUtils.isEmpty(volume?.higherThresh) &&
                DataUtils.isEmpty(volume?.lowerThresh)
            )
        ) {
            const { volume: volumeUnit } = unitSelected;
            if (toSource && !volumeUnit) {
                volume = volume.setUnit(METERS_CUBIC);
            }
            if (volumeUnit) {
                volume = volume.setUnit(volumeUnit);
            }
            volume = getConvertedResource(volume, 'volume');
        }

        if (
            !(
                DataUtils.isEmpty(distance?.higherThresh) &&
                DataUtils.isEmpty(distance?.lowerThresh)
            )
        ) {
            const { distance: distanceUnit } = unitSelected;
            if (toSource && !distanceUnit) {
                distance = distance.setUnit(UnitEnum.METERS);
            }
            if (distanceUnit) {
                distance = distance?.setUnit(distanceUnit);
            }
            distance = getConvertedResource(distance, 'distance');
        }

        if (
            !(
                DataUtils.isEmpty(weight?.higherThresh) &&
                DataUtils.isEmpty(weight?.lowerThresh)
            )
        ) {
            weight = getConvertedResource(weight, 'weight');
        }

        if (
            !(
                DataUtils.isEmpty(travelTime?.higherThresh) &&
                DataUtils.isEmpty(travelTime?.lowerThresh)
            )
        ) {
            travelTime = getConvertedResource(travelTime, 'travelTime');
        }

        if (
            !(
                DataUtils.isEmpty(totalTime?.higherThresh) &&
                DataUtils.isEmpty(totalTime?.lowerThresh)
            )
        ) {
            totalTime = getConvertedResource(totalTime, 'totalTime');
        }

        return this.setVolume(volume)
            .setDistance(distance)
            .setWeight(weight)
            .setTravelTime(travelTime)
            .setTotalTime(totalTime)
            .setLocation(location)
            .setTask(task)
            .setTour(tour);
    }

    convertToSourceUnit(unitConverter, unitSelected): this {
        return this.convertUnit(unitConverter, true, unitSelected);
    }

    convertToPreferredUnit(unitConverter, unitSelected): this {
        return this.convertUnit(unitConverter, false, unitSelected);
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const {
            volume,
            distance,
            weight,
            travelTime,
            totalTime,
            task,
            location,
            tour,
            resourceLimits
        } = this;

        if (volume?.hasValidationErrors?.()) {
            return true;
        }
        if (distance?.hasValidationErrors?.()) {
            return true;
        }
        if (weight?.hasValidationErrors?.()) {
            return true;
        }
        if (travelTime?.hasValidationErrors?.()) {
            return true;
        }

        if (totalTime?.hasValidationErrors?.()) {
            return true;
        }

        if (location?.hasValidationErrors?.()) {
            return true;
        }

        if (task?.hasValidationErrors?.()) {
            return true;
        }
        if (tour?.hasValidationErrors?.()) {
            return true;
        }
        if (resourceLimits) {
            return resourceLimits.some(resource =>
                resource.hasValidationErrors()
            );
        }

        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let {
            volume,
            distance,
            weight,
            travelTime,
            totalTime,
            task,
            location,
            tour,
            resourceLimits
        } = this;
        if (volume) {
            volume = volume.setAllValidationErrors();
        }
        if (distance) {
            distance = distance.setAllValidationErrors();
        }
        if (weight) {
            weight = weight.setAllValidationErrors();
        }
        if (travelTime) {
            travelTime = travelTime.setAllValidationErrors();
        }

        if (totalTime) {
            totalTime = totalTime.setAllValidationErrors();
        }

        if (location) {
            location = location.setAllValidationErrors();
        }

        if (task) {
            task = task.setAllValidationErrors();
        }
        if (tour) {
            tour = tour.setAllValidationErrors();
        }
        if (resourceLimits) {
            resourceLimits = resourceLimits.map(resourceLimit =>
                resourceLimit.setAllValidationErrors()
            );
        }
        return this.setVolume(volume)
            .setDistance(distance)
            .setWeight(weight)
            .setTask(task)
            .setTravelTime(travelTime)
            .setTotalTime(totalTime)
            .setLocation(location)
            .setTour(tour)
            .setResourceLimits(resourceLimits);
    }

    hasNonEmptyValues(): boolean {
        const {
            volume,
            distance,
            weight,
            travelTime,
            totalTime,
            location,
            task,
            resourceLimits
        } = this;
        if (
            !DataUtils.isEmpty(task) ||
            !DataUtils.isEmpty(location) ||
            !DataUtils.isEmpty(volume) ||
            !DataUtils.isEmpty(distance) ||
            !DataUtils.isEmpty(weight) ||
            !DataUtils.isEmpty(travelTime) ||
            !DataUtils.isEmpty(totalTime) ||
            !resourceLimits.isEmpty()
        ) {
            return true;
        }
        return false;
    }

    defaultSanitize(): this {
        const sanitizedRiderPersonaLimits = super.defaultSanitize();
        if (!this.hasNonEmptyValues() && !this.transactionTimePerTask) {
            return null;
        }
        if (sanitizedRiderPersonaLimits) {
            let {
                volume,
                distance,
                weight,
                travelTime,
                totalTime,
                task,
                location,
                tour,
                resourceLimits
            } = this;

            if (volume) {
                volume = this.sanitizeResourceLimit(volume);
            }
            if (distance) {
                distance = this.sanitizeResourceLimit(distance);
            }
            if (weight) {
                weight = this.sanitizeResourceLimit(weight);
            }
            if (travelTime) {
                travelTime = this.sanitizeResourceLimit(travelTime);
            }
            if (totalTime) {
                totalTime = this.sanitizeResourceLimit(totalTime);
            }
            if (task) {
                task = this.sanitizeResourceLimit(task);
            }
            if (location) {
                location = this.sanitizeResourceLimit(location);
            }
            if (tour) {
                tour = this.sanitizeResourceLimit(tour);
            }
            if (resourceLimits) {
                resourceLimits = resourceLimits.filter(
                    limit =>
                        !DataUtils.isEmpty(limit.lowerThresh) ||
                        !DataUtils.isEmpty(limit.higherThresh)
                );
                resourceLimits = resourceLimits.map(resource =>
                    this.sanitizeCustomResourceLimit(resource)
                );
            }

            return sanitizedRiderPersonaLimits
                .setVolume(volume)
                .setDistance(distance)
                .setWeight(weight)
                .setTask(task)
                .setLocation(location)
                .setTravelTime(travelTime)
                .setTotalTime(totalTime)
                .setTour(tour)
                .setResourceLimits(resourceLimits);
        }
        return sanitizedRiderPersonaLimits;
    }

    sanitizeResourceLimit(resource) {
        let updatedResource = resource;
        const isHigherThreshEmpty = DataUtils.isEmpty(resource?.higherThresh);
        const isLowerThreshEmpty = DataUtils.isEmpty(resource?.lowerThresh);
        if (isHigherThreshEmpty && isLowerThreshEmpty) {
            return null;
        }
        if (isHigherThreshEmpty) {
            updatedResource = updatedResource.set('higherThresh', null);
        }
        if (isLowerThreshEmpty) {
            updatedResource = updatedResource.set('lowerThresh', null);
        }
        return updatedResource;
    }

    sanitizeCustomResourceLimit(resource) {
        let updatedResource = resource;
        const isLowerThreshEmpty = DataUtils.isEmpty(resource?.lowerThresh);
        if (isLowerThreshEmpty) {
            updatedResource = updatedResource.set('lowerThresh', 0);
        }
        return updatedResource;
    }

    static defaultInit(): InternalRiderPersonaLimits {
        return InternalRiderPersonaLimits.fromJS({})
            .setVolume(null)
            .setDistance(null)
            .setWeight(null)
            .setTravelTime(null)
            .setTask(null)
            .setLocation(null)
            .setTour(null)
            .setTotalTime(null)
            .setTransactionTimePerTask(0);
    }

    get volume(): ?VolumeLimit {
        return this._state.get('volume');
    }

    get distance(): ?RiderPersonaResourceLimit {
        return this._state.get('distance');
    }

    get weight(): ?RiderPersonaResourceLimit {
        return this._state.get('weight');
    }

    get travelTime(): ?RiderPersonaResourceLimit {
        return this._state.get('travelTime');
    }

    get task(): ?RiderPersonaResourceLimit {
        return this._state.get('task');
    }

    get tour(): ?RiderPersonaResourceLimit {
        return this._state.get('tour');
    }

    @validate
    setTransactionTimePerTask(transactionTimePerTask: ?number): this {
        return super.setTransactionTimePerTask(transactionTimePerTask);
    }

    validateTransactionTimePerTask(): ?string {
        const { transactionTimePerTask } = this;
        if (
            !DataUtils.isEmpty(transactionTimePerTask) &&
            !NumberUtils.isNonNegativeNumber(transactionTimePerTask)
        ) {
            return nsTranslate(
                'riderPersonaResourceLimit.limitNotANegativeNumber',
                {
                    name: nsTranslate(
                        'masterRiderPersonaProperties.resourceLimits.transactionTimePerTask'
                    )
                }
            );
        }
    }

    get totalTime(): ?RiderPersonaResourceLimit {
        return this._state.get('totalTime');
    }

    get location(): ?RiderPersonaResourceLimit {
        return this._state.get('location');
    }
}

export { InternalRiderPersonaLimits as RiderPersonaLimits };
